import { env } from '@/env'
import { api } from '@/lib/api-client'

export type TrackingEventName = 'page_view' | 'deck_open'
export type TrackingConsent = 'granted' | 'denied'

export interface EventOrigin {
  referrer: string | null
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmTerm: string | null
  utmContent: string | null
}

export interface TrackEventOptions {
  productId?: string | null
  businessUnitId?: string | null
  metadata?: Record<string, unknown> | null
}

export interface IdentifyInput {
  email: string
  name?: string | null
  phone?: string | null
}

export interface VisitorContactResponse {
  id: string
  visitorId: string
  email: string
  name: string | null
  phone: string | null
  createdAt: string
}

const CONSENT_KEY = 'enjoy.trackingConsent'
const VISITOR_KEY = 'enjoy.visitorId'
const SESSION_KEY = 'enjoy.session'
const SESSION_WINDOW_MS = 30 * 60 * 1000

interface StoredSession {
  id: string
  lastActivityAt: number
}

export function parseUtm(search: string): Omit<EventOrigin, 'referrer'> {
  const params = new URLSearchParams(search)
  return {
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    utmTerm: params.get('utm_term'),
    utmContent: params.get('utm_content'),
  }
}

export function isSessionExpired(lastActivityAt: number, now: number): boolean {
  return now - lastActivityAt > SESSION_WINDOW_MS
}

export function parseSession(raw: string | null): StoredSession | null {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as StoredSession).id === 'string' &&
      typeof (parsed as StoredSession).lastActivityAt === 'number'
    ) {
      return parsed as StoredSession
    }
    return null
  } catch {
    return null
  }
}

export function getTrackingConsent(): TrackingConsent | null {
  const value = localStorage.getItem(CONSENT_KEY)
  if (value === 'granted' || value === 'denied') return value
  return null
}

export function hasTrackingConsent(): boolean {
  return getTrackingConsent() === 'granted'
}

export function setTrackingConsent(granted: boolean): void {
  localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied')
  if (!granted) {
    localStorage.removeItem(VISITOR_KEY)
    localStorage.removeItem(SESSION_KEY)
  }
}

function getOrCreateVisitorId(): string {
  const existing = localStorage.getItem(VISITOR_KEY)
  if (existing) return existing
  const id = crypto.randomUUID()
  localStorage.setItem(VISITOR_KEY, id)
  return id
}

function getOrCreateSession(now: number): { id: string; isEntry: boolean } {
  const current = parseSession(localStorage.getItem(SESSION_KEY))
  if (current && !isSessionExpired(current.lastActivityAt, now)) {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ id: current.id, lastActivityAt: now }))
    return { id: current.id, isEntry: false }
  }
  const id = crypto.randomUUID()
  localStorage.setItem(SESSION_KEY, JSON.stringify({ id, lastActivityAt: now }))
  return { id, isEntry: true }
}

function captureOrigin(): EventOrigin {
  return {
    referrer: document.referrer === '' ? null : document.referrer,
    ...parseUtm(window.location.search),
  }
}

function trackingBaseUrl(): string {
  return import.meta.env.DEV ? '' : env.VITE_API_BASE_URL
}

function dispatch(url: string, body: string): void {
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const queued = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
    if (queued) return
  }
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    credentials: 'include',
  }).catch((err: unknown) => {
    if (import.meta.env.DEV) console.warn('[tracking] beacon fallback failed', err)
  })
}

export function trackEvent(eventName: TrackingEventName, options: TrackEventOptions = {}): void {
  if (!hasTrackingConsent()) return

  const visitorId = getOrCreateVisitorId()
  const session = getOrCreateSession(Date.now())

  const payload = {
    visitorId,
    sessionId: session.id,
    eventName,
    productId: options.productId ?? null,
    businessUnitId: options.businessUnitId ?? null,
    occurredAt: new Date().toISOString(),
    metadata: options.metadata ?? null,
    ...(session.isEntry ? { origin: captureOrigin() } : {}),
  }

  dispatch(`${trackingBaseUrl()}/api/tracking/events`, JSON.stringify(payload))
}

export async function identify(input: IdentifyInput): Promise<VisitorContactResponse> {
  const visitorId = getOrCreateVisitorId()
  const { data } = await api.post<{ contact: VisitorContactResponse }>('/api/tracking/identify', {
    visitorId,
    email: input.email,
    name: input.name ?? null,
    phone: input.phone ?? null,
  })
  return data.contact
}
